import { getConfig } from '../../configs';
import logging from '../logging';
const config = getConfig();

/**
 * A lightweight event bus to manage analytics events.
 * Enables event publishing, subscribing, and global event tracking.
 */
class AnalyticsEventBus {
  /**
   * Stores event-specific subscribers.
   * Each event has an array of callback functions that will be triggered when the event is published.
   */
  private subscribeEvents: Record<string, Function[]> = {};

  /**
   * Stores global subscribers that listen to all published events.
   */

  private globalSubscribers: Function[] = [];
  /**
   * Maintains a log of all published events and their payloads.
   * Useful for debugging and analytics tracking.
   */
  private eventBusRecords: { event: string; data?: any }[] = [];

  constructor() {
    if (config.analyticsEventBus) {
      Object.assign(this, config.analyticsEventBus);
    }
  }

  /**
   * Subscribes a callback function to a specific event.
   *
   * @param event - The name of the event to listen for.
   * @param callback - The function to execute when the event is published.
   * @returns Nothing.
   */
  subscribe(event: string, callback: Function) {
    if (!this.subscribeEvents[event]) this.subscribeEvents[event] = [];
    this.subscribeEvents[event].push(callback);
  }

  /**
   * Subscribes a callback function to listen for all published events.
   *
   * @param callback - The function to execute for every event published.
   * @returns Nothing.
   */
  subscribeAll(callback: Function) {
    this.globalSubscribers.push(callback);
  }

  /**
   * Publishes an event and notifies all subscribers.
   * Logs the event for tracking purposes.
   *
   * @param event - The name of the event to publish.
   * @param payload - Optional data associated with the event.
   * @returns Nothing.
   */
  publish(event: string, payload?: any) {
    logging.info(
      `📢 Event Published: "${event}"`,
      `\n🔗 Origin: ${window.location.hostname}`,
      `\n📍 Path: ${window.location.pathname}`,
      '\n📦 Payload:',
      payload
    );

    // Record the event for history tracking
    this.eventBusRecords.push({ event, data: payload });

    // Notify global subscribers
    this.globalSubscribers.forEach((callback) => callback(event, payload));

    if (!this.subscribeEvents[event]) return;
    this.subscribeEvents[event].forEach((callback) => callback(payload));
  }

  /**
   * Retrieves a history of all published events.
   * Useful for debugging and analytics tracking.
   *
   * @returns An array of published event records.
   */
  getEventBusRecords() {
    return this.eventBusRecords;
  }
}

const analyticsEventBus = new AnalyticsEventBus();

export default analyticsEventBus;
